import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Cloud, HardDrive, FileText, ImageIcon, ReceiptText, RefreshCw, Database } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODULE_META = {
  design_documents: { label: "Design Documents", icon: ImageIcon, color: "bg-purple-100 text-purple-700" },
  project_documents: { label: "Project Documents", icon: FileText, color: "bg-blue-100 text-blue-700" },
  kyc_documents: { label: "Employee KYC", icon: FileText, color: "bg-amber-100 text-amber-700" },
  finance_invoices: { label: "Finance Invoices", icon: ReceiptText, color: "bg-emerald-100 text-emerald-700" },
};

export default function StorageStats() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/storage-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load storage stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/storage-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!ignore) setData(res.data);
      } catch (e) {
        if (!ignore) toast.error(e.response?.data?.detail || "Failed to load storage stats");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [token]);

  if (loading || !data) {
    return <div className="p-6 text-slate-500 dark:text-slate-400">Loading storage stats…</div>;
  }

  const cloud = data.cloudinary || {};
  const isConfigured = cloud.configured;
  const usagePct = cloud.storage_pct || 0;
  const bwPct = cloud.bandwidth_pct || 0;
  const usageBarColor = usagePct > 85 ? "bg-red-500" : usagePct > 60 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="space-y-6" data-testid="storage-stats">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Database className="h-5 w-5 text-brillien-600" /> Storage Dashboard
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Cloudinary usage and per-module file inventory.
          </p>
        </div>
        <Button variant="outline" onClick={fetchStats} data-testid="storage-refresh">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Cloudinary Account Card */}
      <Card className="p-6 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Cloud className="h-4 w-4 text-brillien-600" /> Cloudinary Account
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Plan: <Badge variant="outline" className="ml-1">{cloud.plan || "—"}</Badge>
            </p>
          </div>
          {isConfigured ? (
            <Badge className="bg-emerald-100 text-emerald-700">Connected</Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700">Not Configured</Badge>
          )}
        </div>

        {isConfigured && cloud.storage_bytes !== null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Storage</span>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {cloud.storage_human} / {cloud.storage_limit_human}
                </span>
              </div>
              <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className={`h-full ${usageBarColor} transition-all`} style={{ width: `${Math.min(100, usagePct)}%` }} />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{usagePct.toFixed(2)}% used</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Bandwidth (this month)</span>
                <span className="text-sm text-slate-600 dark:text-slate-400">{cloud.bandwidth_human}</span>
              </div>
              <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${Math.min(100, bwPct)}%` }} />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {bwPct.toFixed(2)}% of free quota
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {cloud.error || "Cloudinary stats unavailable. Check backend env vars."}
          </p>
        )}

        {cloud.credits_used !== undefined && cloud.credits_used !== null && (
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Cloudinary credits: <span className="font-mono">{cloud.credits_used}</span> / {cloud.credits_limit}
            </p>
          </div>
        )}
      </Card>

      {/* Per-Module Card */}
      <Card className="p-6 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-brillien-600" /> Per-Module Inventory
          </h3>
          <div className="flex items-center gap-3 text-xs">
            <Badge className="bg-emerald-100 text-emerald-700">
              {data.totals.cloud_count} on cloud
            </Badge>
            <Badge className="bg-amber-100 text-amber-700">
              {data.totals.legacy_count} legacy / local
            </Badge>
            <Badge variant="outline">Total: {data.totals.count} files · {data.totals.total_bytes_human}</Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(data.modules).map(([key, m]) => {
            const meta = MODULE_META[key] || { label: key, icon: FileText, color: "bg-slate-100 text-slate-700" };
            const Icon = meta.icon;
            return (
              <div
                key={key}
                className="p-4 rounded-lg bg-slate-50 dark:bg-slate-700/40 border border-slate-100 dark:border-slate-600"
                data-testid={`storage-module-${key}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${meta.color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{meta.label}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{m.total_bytes_human}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{m.count}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">files</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="text-emerald-600">{m.cloud_count} cloud</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-amber-600">{m.legacy_count} legacy</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
